import { Chip, Paper, styled } from '@ethings/ui-toolkit';
import { Box, IconButton, useTheme } from '@mui/material';
import { TFunction } from 'i18next';
import { FC, useEffect, useRef, useState } from 'react';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import { AppliedFilter, AppliedFilterType } from '../entity-search/types';
import { Icon } from '../icons';
import { FilterElement } from './filter-element';

interface Props {
  filters: AppliedFilter<AppliedFilterType>[];
  openFilter?: boolean;
  removeAllFilters: () => unknown;
  removeFilter: (filterToRemove: AppliedFilter) => unknown;
  tFunction: TFunction;
}

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  height: '2rem',
  margin: '0 0.5rem 0.7rem 0',
  width: '2rem',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: '0 0.5rem 0.7rem 0',
  backgroundColor: theme.palette.error.light,
  color: theme.palette.common.white,
}));

const StyledPaper = styled(Paper)(() => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden',
  overscrollBehaviorX: 'contain',
  paddingTop: '8px',
  scrollbarColor: 'transparent transparent',
  '&::-webkit-scrollbar': { display: 'none' },
  whiteSpace: 'nowrap',
}));

export const AppliedFiltersMapMobile: FC<Props> = ({ filters, removeAllFilters, removeFilter, tFunction }) => {
  const theme = useTheme();
  const timeoutRef = useRef<number>(undefined);
  const startScroll = useRef<HTMLDivElement>(null);
  const [disabled, setDisabled] = useState(false);
  const label = tFunction('common:component.filter.actions.clear-all');
  const handleScrollBack = (): void => {
    setDisabled(true);
    scrollIntoView(startScroll.current as Element, { behavior: 'smooth', inline: 'end' });
    timeoutRef.current = window.setTimeout(() => setDisabled(false), 500);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <StyledPaper>
      <Box ref={startScroll} sx={{ px: 0.5 }} />
      <StyledChip
        disabled={disabled}
        label={label}
        onDelete={removeAllFilters}
      />

      {
        filters.map((filter) => {
          return <FilterElement
            key={filter.id}
            disabled={disabled}
            filter={filter}
            onRemoveClicked={() => removeFilter(filter)}
            sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.common.white }}
          />;
        })
      }

      {
        filters.length > 3
        && <StyledIconButton onTouchEnd={handleScrollBack}>
          <Icon icon="ArrowLeft" />
        </StyledIconButton>
      }
      <Box sx={{ px: 0.5 }} />
    </StyledPaper>
  );
};

export const AppliedFiltersMapDesktop: FC<Props> = ({ filters, removeAllFilters, removeFilter, tFunction }) => {
  const theme = useTheme();
  const label = tFunction('common:component.filter.actions.clear-all');
  return (
    <Box sx={{ display: 'flex', width: '100%', flex: 1, p: 1, zIndex: 999 }}>
      <Box>
        <StyledChip
          label={label}
          onDelete={removeAllFilters}
        />
        {
          filters.map((filter) => {
            return <FilterElement
              key={filter.id}
              filter={filter}
              onRemoveClicked={() => removeFilter(filter)}
              sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.common.white }}
            />;
          })
        }
      </Box>
    </Box>
  );
};
